import { chain, filter, find, isEqual, map, orderBy, pick, uniq } from 'lodash';
import { type OrderConfig, type CustomSortConfig } from 'common/visualizations/vif';
import { type HeaderRow, type NormalRow, type Row, type SubTotalRow } from './type';
import { aggregateRows } from './gridDataAggregator';
import { type TableColumn } from '../types';
import { isCustomSortEnabled } from 'common/visualizations/helpers/VifSelectors';
import { tableActionValueKey } from 'common/visualizations/views/agGridReact/helpers/tableActionsHelper';
import { SoQLType } from 'common/types/soql';

/**
 * For each table-action field, stores the group's single uniform value (or
 * `null` when its leaf rows disagree) under the shared `__tableActionValue__<field>`
 * key. This mirrors the aggregate the live grid injects server-side, so the PDF
 * hyperlink resolver can decide whether a group row has one linkable value the
 * same way the grid does.
 */
const resolveUniformActionValues = (
  leafRows: NormalRow[],
  actionColumnFields: string[],
): Record<string, string | number | null> =>
  actionColumnFields.reduce<Record<string, string | number | null>>((acc, field) => {
    const distinctValues = uniq(
      leafRows.map((row) => row[field]).filter((value) => value != null && value !== ''),
    );
    acc[tableActionValueKey(field)] = distinctValues.length === 1 ? distinctValues[0] : null;
    return acc;
  }, {});

export const groupRows = (
  rows: NormalRow[],
  tableColumns: TableColumn[],
  orderConfigs: OrderConfig[] = [],
  showSubTotal = false,
  columnSortConfig: CustomSortConfig | undefined,
  actionColumnFields: string[] = [],
  hideDuplicateGroups = false,
): Row[] => {
  const groupSort = filter(tableColumns, (c) => c.isGrouped);
  const groupByFields = map(groupSort, (c) => c.field);
  const withDuplicatesHidden = (grouped: Row[]) =>
    hideDuplicateGroups ? removeDuplicateGroupRows(grouped, groupByFields) : grouped;
  if (!isCustomSortEnabled()) {
    const sortColumns = map(orderConfigs, 'columnName');
    const sortOrders = map(orderConfigs, (s) => (s.ascending ? 'asc' : 'desc'));
    const sortedRows = sortOrders.length > 0 ? orderBy(rows, sortColumns, sortOrders) : rows;

    if (groupByFields.length <= 0) {
      return sortedRows;
    }
    return withDuplicatesHidden(
      processGrouping(
        sortedRows,
        groupByFields,
        tableColumns,
        showSubTotal,
        sortColumns,
        sortOrders,
        actionColumnFields,
      ),
    );
  } else {
    const reorderedSortConfig = orderBy(orderConfigs, (config) => {
      const idx = groupByFields.indexOf(config.columnName);
      return idx === -1 ? Infinity : idx;
    });
    const sortColumns = map(reorderedSortConfig, (config) => config.columnName);
    const sortOrders = map(reorderedSortConfig, (s) => {
      if (s.sortToFollow == 'descending') {
        return 'desc';
      }
      return 'asc';
    });
    const iteratees = customSortIteratees(sortColumns, columnSortConfig, tableColumns, reorderedSortConfig);
    const sortedRows = orderBy(rows, iteratees, sortOrders);
    if (groupByFields.length <= 0) {
      return sortedRows;
    }
    return withDuplicatesHidden(
      processGrouping(
        rows,
        groupByFields,
        tableColumns,
        showSubTotal,
        iteratees,
        sortOrders,
        actionColumnFields,
      ),
    );
  }
};

/**
 * Mirrors the live grid's hide-duplicate-groups behavior (Grid.tsx
 * `isDuplicateGroupNode`): a nested group whose value equals its parent group's
 * value is hidden. Drops the header and subtotal rows for such groups while
 * keeping their leaf rows, so the printed output matches the on-screen table.
 */
const removeDuplicateGroupRows = (rows: Row[], groupByFields: string[]): Row[] =>
  rows.filter((row) => {
    if (row._rowType !== 'header' && row._rowType !== 'subTotal') {
      return true;
    }
    const level = row._groupLevel ?? -1;
    if (level <= 0) {
      return true;
    }
    return row[groupByFields[level]] !== row[groupByFields[level - 1]];
  });

const customSortIteratees = (
  sortColumns: string[],
  columnSortConfig: CustomSortConfig | undefined,
  tableColumns: TableColumn[],
  reorderedSortConfig: OrderConfig[] = [],
) => {
  return sortColumns.map((field) => {
    const regularSortConfig = find(reorderedSortConfig, ['columnName', field]);
    if (columnSortConfig === undefined || regularSortConfig?.sortToFollow != 'customSort') {
      return field;
    }
    const orderList = columnSortConfig[field];
    const fieldColumn = find(tableColumns, { field: field });
    if (orderList) {
      return (item: { [x: string]: any }) => {
        let value = item[field];
        if (isEqual(SoQLType.SoQLNumberT, fieldColumn?.type) && value !== undefined) {
          value = value.toString();
        }
        const index = orderList.indexOf(value);
        return index === -1 ? Number.MAX_SAFE_INTEGER : index;
      };
    }
    return field;
  });
};

const processGrouping = (
  childRows: Row[],
  currentGroupByFields: string[],
  tableColumns: TableColumn[],
  showSubTotal = false,
  sortColumns: (string | ((item: { [x: string]: any }) => number))[],
  sortOrders: ('asc' | 'desc')[] | [],
  actionColumnFields: string[] = [],
): Row[] => {
  const result = chain(childRows)
    .groupBy((row) => chain(row).pick(currentGroupByFields).values().value().join('-'))
    .map((groupedRows) => {
      const groupBys = pick(groupedRows[0], currentGroupByFields);
      const aggregations = aggregateRows(groupedRows, tableColumns);
      // Only leaf rows carry the raw column values; deeper header rows from a
      // prior recursion pass are excluded, matching how aggregateRows works.
      const leafRows = groupedRows.filter((row) => row._rowType === undefined);
      const actionValues = resolveUniformActionValues(leafRows, actionColumnFields);
      return {
        ...groupBys,
        ...aggregations,
        _headerRow: buildHeaderRow(groupBys, currentGroupByFields, aggregations, actionValues),
        _rows: groupedRows,
        _subTotalRow: buildSubTotalRow(groupBys, aggregations, currentGroupByFields),
      };
    })
    .orderBy(sortColumns, sortOrders)
    .flatMap((group) => [group._headerRow, ...group._rows, ...(showSubTotal ? [group._subTotalRow] : [])])
    .value();

  if (currentGroupByFields.length <= 1) {
    return result;
  }

  return processGrouping(
    result,
    currentGroupByFields.slice(0, -1),
    tableColumns,
    showSubTotal,
    sortColumns,
    sortOrders,
    actionColumnFields,
  );
};

const buildHeaderRow = (
  groupBys: Partial<NormalRow>,
  groupByFields: string[],
  aggregations: object,
  actionValues: Record<string, string | number | null> = {},
): HeaderRow => ({
  ...groupBys,
  ...aggregations,
  ...actionValues,
  _rowType: 'header',
  _groupLevel: groupByFields.length - 1,
});

const buildSubTotalRow = (
  groupBys: Partial<NormalRow>,
  aggregations: Partial<NormalRow>,
  groupByFields: string[],
): SubTotalRow => ({
  ...groupBys,
  ...aggregations,
  _rowType: 'subTotal',
  _groupLevel: groupByFields.length - 1,
});
